import type { ClarifyingQuestion, GeneratedPrd } from '../types';

export function generatePrd(
  summary: string,
  questions: ClarifyingQuestion[],
  userAnswers: Record<string, string>,
  revisionNote?: string
): GeneratedPrd {
  const answered = questions.map((q) => ({
    question: q.question,
    answer: userAnswers[q.id]?.trim() || q.defaultAnswer,
  }));

  const title = summary.replace(/^[Bb]uild (a |an )?/, '').replace(/\.$/, '').trim() || 'New Feature';

  const goal = revisionNote
    ? `${summary} This revised version also incorporates: ${revisionNote}`
    : summary;

  const usersAndCoreFlows = `**Primary users:** Product managers, engineers, and designers discussing features in a Slack thread.

1. **Start capture** — A user @mentions the bot in a feature discussion thread. The bot reads the visible thread history and replies with clarifying questions and default answers.
2. **Answer & approve** — The user reviews or edits the default answers and replies \\"approved\\". The bot generates a \\"prd.md\\" file and posts a summary back to the thread.
3. **Revise** — After approval, the user posts revision feedback (for example, \\"add offline mode\\"), and the bot rewrites the PRD in place, posting an updated summary and file.`;

  const requirements = `**Must-haves (v1):**
1. The bot responds to an @mention inside any thread it has access to.
2. It summarizes the thread history into a one-sentence feature description.
3. It asks at most 5 clarifying questions, each with a sensible default answer.
4. It generates a PRD containing: Goal, Users & core flows, Requirements, Screens, Data, Open questions.
5. It posts a brief Slack mrkdwn summary plus the \\"prd.md\\" file as a thread reply.
6. It recognizes \\"approved\\" and revision-style replies and updates the PRD accordingly.
7. All Slack data is mocked inline; the prototype is client-side only.

**Clarifying answers:**
${answered.map((a, i) => `${i + 1}. ${a.question}\n   ${a.answer}`).join('\n\n')}

**Nice-to-haves (out of v1):**
- Real Slack OAuth/app installation.
- Persistent storage of PRDs across sessions.
- Multi-language support.
- Automatic Jira/GitHub issue creation.`;

  const screens = `This is a conversational Slack bot; there are no traditional screens. Key interaction surfaces:

- **Thread mention** — user types \\"@prd-bot\\" in a thread.
- **Clarification reply** — bot posts a numbered list of questions with default answers.
- **Approval message** — user replies \\"approved\\".
- **PRD summary reply** — bot posts concise mrkdwn summary and a file attachment.
- **Revision reply** — user posts feedback; bot posts updated summary and file.`;

  const data = `**Thread history** — hardcoded mock array of 3–5 Slack messages (user name, timestamp, text).\n**Generated feature summary** — produced from the mock thread by a local rule/heuristic.\n**Clarifying questions & defaults** — hardcoded template set selected based on keywords in the thread.\n**PRD markdown** — generated into the local artifact path and displayed as a mock file attachment.\n**Approval state** — local boolean flag toggled by the \\"approved\\" reply.`;

  const openQuestions = `1. Should the bot also support a \\"/prd\\" slash command, or only thread mentions? *Default: thread mentions only.*\n2. Should every clarifying question be answered before generating the PRD? *Default: no — defaults ride for unanswered questions.*\n3. Should the bot support multiple PRDs in one thread? *Default: no — one PRD per thread in v1.*`;

  return {
    title,
    goal,
    usersAndCoreFlows,
    requirements,
    screens,
    data,
    openQuestions,
  };
}

export function prdToMarkdown(prd: GeneratedPrd): string {
  return `# PRD: ${prd.title}

## Goal

${prd.goal}

## Users & core flows

${prd.usersAndCoreFlows}

## Requirements

${prd.requirements}

## Screens

${prd.screens}

## Data

${prd.data}

## Open questions

${prd.openQuestions}
`;
}
